import styled, { createGlobalStyle } from 'styled-components';
import { createPortal } from 'react-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useViewedVehicle } from '../../hooks/useViewedVehicle';
import { useImages } from 'services/viewVehicleSlice/viewVehicleSlice';
import { useGetVehicleId } from 'pages/CreateVehicle/hooks/useGetVehicleId';
import { useGetSectionsQuery } from 'services/api/vehicle-sections';
import { Section } from 'services/api/vehicle-sections/types';
import { useCurrentUser } from 'hooks/selectors';
import { useGetListerRouteDistancesQuery } from 'services/api/list-vehicles';
import { formatAddress } from 'pages/Admin/ListerDashboard/utils/formatAddress';
import { formatCurrency } from 'utils';
import { formatYearAndMonth } from 'utils/dates';
import { AutoCheckReportIcon } from '../../icons/AutoCheckReportIcon';
import { TrimIcon, EngineIcon, ExteriorIcon, TransmissionIcon, DriveTrainIcon } from '../VehicleCharacteristics/icons';
import { DollarIcon, MoneyIcon, GasStationIcon } from '../VehicleInfo/BlueBookInfo/icons';
import {
  CheckConfirmedIcon,
  FailIcon,
  InfoIcon as AutoCheckInfoIcon,
  OwnerIcon,
  AccidentIcon,
  StateTitleBrandIcon,
  UsageTypeIcon,
  OdometerCheckIcon
} from '../AutoCheckReport/autoCheckReportIcons';
import { NotesIcon } from 'pages/ListerVehicleUploadV2/icons/notes';
import { EmptyAvatarPlaceholder } from 'components/Icons';
import { PhoneNumberText } from 'components';
import { ListerIcon, AddressIcon, PhoneIcon, HomeIcon, CarsArriveIcon } from '../../icons/InfoIcons';
import { useExteriorGeneralSection } from '../VehicleSections/hooks/useExteriorGeneralSection';
import { useTiresSection } from '../VehicleSections/hooks/useTiresSection';
import { useExteriorDamageImages } from '../VehicleSections/hooks/useExteriorDamageImages';
import { useInteriorGeneralSection } from '../VehicleSections/hooks/useInteriorGeneralSection';
import { usePowerEquipmentSection } from '../VehicleSections/hooks/usePowerEquipmentSection';
import { useInteriorDamageImages } from '../VehicleSections/hooks/useInteriorDamageImages';
import { useUnderHoodSection } from '../VehicleSections/hooks/useUnderHoodSection';
import { useDashSection } from '../VehicleSections/hooks/useDashSection';
import { useDriveCyclesSection } from '../VehicleSections/hooks/useDriveCyclesSection';
import { useOBDImages } from '../VehicleSections/hooks/useOBDImages';

// ─── global print styles ────────────────────────────────────────────────────

const PrintGlobalStyle = createGlobalStyle`
  @media print {
    @page {
      margin: 14mm;
      @top-left { content: none; }
      @top-center { content: none; }
      @top-right { content: none; }
      @bottom-left { content: none; }
      @bottom-center { content: none; }
      @bottom-right { content: none; }
    }
    body > * { display: none !important; }
    body > .print-view { display: block !important; }
  }
`;

// ─── root wrapper ────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  display: none;
  @media print {
    display: block;
    font-family: Roboto, sans-serif;
    color: #000;
    font-size: 13px;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

// ─── hero ────────────────────────────────────────────────────────────────────

const HeroImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 260px;
  overflow: hidden;
  margin-bottom: 20px;
  break-inside: avoid;
`;

const HeroImage = styled.img`
  display: block;
  height: 100%;
  width: auto;
`;

const VehicleTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 4px 0;
`;

const VinText = styled.p`
  font-size: 12px;
  color: #999;
  margin: 0 0 18px 0;
`;

// ─── KBB stat cards ──────────────────────────────────────────────────────────

const StatCardsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const StatCard = styled.div<{ bg: string }>`
  flex: 1;
  background: ${p => p.bg};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 10px 12px;
  gap: 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const StatIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatValue = styled.div`
  font-size: 15px;
  font-weight: 700;
  margin-top: 8px;
`;

const StatLabel = styled.div`
  font-size: 9px;
  font-weight: 400;
  color: #555;
  text-align: center;
  margin-top: 4px;
  max-width: 80px;
`;

// ─── section chrome ──────────────────────────────────────────────────────────

const SectionGap = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px 0;
`;

const BlockTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

// ─── AutoCheck ───────────────────────────────────────────────────────────────

const AutoCheckBlock = styled.div`
  background: rgba(249, 249, 249, 1);
  border-radius: 30px;
  padding: 12px 16px;
  break-inside: avoid;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const AutoCheckLogoRow = styled.div`
  margin-bottom: 8px;
`;

const PrintBanner = styled.div<{ bg: string; fg: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.bg};
  color: ${p => p.fg};
  font-size: 8px;
  height: 24px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const AutoCheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

const AutoCheckIconBox = styled.div`
  width: 38px;
  height: 38px;
  border: 1px solid rgba(204, 204, 204, 0.8);
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const AutoCheckLabel = styled.span`
  font-weight: 700;
  font-size: 14px;
  flex: 1;
`;

const AutoCheckValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #225a91;
  text-transform: capitalize;
`;

// ─── vehicle characteristics ─────────────────────────────────────────────────

const CharGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 0;
`;

const CharCard = styled.div`
  background: rgba(249, 249, 249, 1);
  border-radius: 20px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const CharIconWrapper = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 24px;
    height: 24px;
  }
`;

const CharLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  margin-top: 8px;
`;

const CharValue = styled.div`
  font-size: 10px;
  font-weight: 400;
  color: #333;
`;

// ─── equipment ───────────────────────────────────────────────────────────────

const EquipmentBlock = styled.div`
  background: rgba(249, 249, 249, 1);
  border-radius: 20px;
  padding: 16px 20px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const EquipmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px 16px;
`;

const EquipmentItem = styled.div`
  font-size: 12px;
  padding: 3px 0;
`;

// ─── inspection sections ──────────────────────────────────────────────────────

const VehicleSectionBlock = styled.div`
  margin-bottom: 20px;
  break-inside: avoid;
`;

const ColumnsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const ColumnCard = styled.div`
  background: rgba(249, 249, 249, 1);
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  break-inside: avoid;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const ColumnHeader = styled.div`
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #000;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ItemCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  gap: 6px;
  height: 40px;
  background: #ffffff;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const ExpirationText = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ItemIconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ItemLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #000;
  flex: 1;
`;

const CheckboxItem = styled.div`
  font-size: 11px;
  color: #555;
  padding: 2px 0 2px 22px;
`;

const gradeToColor: Record<string, { bg: string; fg: string }> = {
  fair: { bg: '#FFC709', fg: '#000' },
  good: { bg: '#1AB13E', fg: '#fff' },
  poor: { bg: '#D01C2E', fg: '#fff' }
};

const GradeBadge = styled.span<{ bg: string; fg: string }>`
  background: ${p => p.bg};
  color: ${p => p.fg};
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 100px;
  white-space: nowrap;
  text-transform: capitalize;
  flex-shrink: 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
`;

// ─── seller / transport ───────────────────────────────────────────────────────

const InfoBlock = styled.div`
  background: rgba(249, 249, 249, 1);
  border-radius: 20px;
  padding: 16px 20px;
  break-inside: avoid;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

const TwoColBlocks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  gap: 8px;
`;

const InfoRowLabel = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #555;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const InfoRowIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 18px;
    height: 18px;
  }
`;

const InfoRowValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  text-align: right;
`;

const DistanceRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 0;
`;

const DistanceText = styled.span`
  font-size: 13px;
  font-weight: 600;
  flex: 1;
`;

const HelperText = styled.div`
  font-size: 10px;
  color: #666;
  margin-top: 2px;
`;

// ─── section notes ────────────────────────────────────────────────────────────

const SectionNoteText = styled.div`
  background: rgba(34, 34, 34, 0.04);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.5;
  color: #000;
  padding: 5px 9px;
  margin: 2px 8px 4px;
  word-wrap: break-word;
`;

// ─── inspector comments ───────────────────────────────────────────────────────

const CommentText = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
  color: #444;
`;

// ─── payment / sale terms ─────────────────────────────────────────────────────

const PaymentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 6px 0;
  gap: 16px;
`;

const PaymentLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  flex-shrink: 0;
`;

const PaymentValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  text-align: right;
`;

const SaleTermsWrapper = styled.div`
  text-align: center;
  padding: 14px 20px;
`;

const SaleTermsTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const SaleTermsText = styled.div`
  font-size: 12px;
  line-height: 1.6;
`;

const HighlightedText = styled.span<{ isGuaranteed?: boolean }>`
  font-weight: 700;
  color: ${p => (p.isGuaranteed ? 'rgba(69, 155, 74, 1)' : 'rgba(175, 41, 48, 1)')};
`;

// ─── lister information ───────────────────────────────────────────────────────

const ListerRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const ListerAvatar = styled.div`
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  svg,
  img {
    width: 64px;
    height: 64px;
    border-radius: 50%;
  }
`;

const ListerName = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const ListerBio = styled.div`
  font-size: 12px;
  color: rgba(97, 97, 97, 1);
  margin-top: 4px;
  line-height: 1.5;
`;

// ─── photo grid ───────────────────────────────────────────────────────────────

const PhotosSection = styled.div`
  break-before: page;
`;

const PhotosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const PhotoItem = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 6px;
`;

// ─── sectioned image block ────────────────────────────────────────────────────

const SectionedPhotosSection = styled.div`
  break-before: page;
`;

const ImageGroupBlock = styled.div`
  margin-bottom: 20px;
`;

const ImageSubGroupBlock = styled.div`
  margin-bottom: 12px;
  break-inside: avoid;
`;

const ImageSubGroupLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
`;

const ImageStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const SectionedPhoto = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 6px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

// ─── helpers ──────────────────────────────────────────────────────────────────

const renderColumn = (section: Section) => {
  const items = Object.values(section.subsections || {}).filter(item => !!item.grade);
  if (items.length === 0) return null;
  const colors = gradeToColor[section.grade || ''] || null;
  return (
    <ColumnCard key={section.id}>
      <ColumnHeader>
        {section.label}
        {colors && (
          <GradeBadge bg={colors.bg} fg={colors.fg}>
            {section.grade}
          </GradeBadge>
        )}
      </ColumnHeader>
      {items.map(item => renderItem(item))}
    </ColumnCard>
  );
};

const renderTopLevelAsColumn = (section: Section | undefined) => {
  if (!section) return null;
  const items = Object.values(section.subsections || {}).filter(item => !!item.grade);
  if (items.length === 0) return null;
  return (
    <ColumnCard key={section.id}>
      <ColumnHeader>{section.label}</ColumnHeader>
      {items.map(item => renderItem(item))}
    </ColumnCard>
  );
};

const renderItem = (item: Section) => {
  const itemColors = item.grade ? gradeToColor[item.grade] : null;
  const selectedCheckboxes = (item.checkboxes || []).filter(c => c.selected);
  return (
    <ItemCard key={item.id}>
      <ItemRow>
        <ItemIconWrapper>
          <NotesIcon />
        </ItemIconWrapper>
        <ItemLabel>{item.label}</ItemLabel>
        {itemColors && (
          <GradeBadge bg={itemColors.bg} fg={itemColors.fg}>
            {item.grade}
          </GradeBadge>
        )}
      </ItemRow>
      {item.notes?.message && <SectionNoteText>{item.notes.message}</SectionNoteText>}
      {selectedCheckboxes.map(cb => (
        <CheckboxItem key={cb.id}>{cb.label}</CheckboxItem>
      ))}
    </ItemCard>
  );
};

const renderOBDColumn = (sections: ReturnType<typeof useGetSectionsQuery>['data']) => {
  if (!sections) return null;
  const obd = sections.interior_questions?.subsections?.odb_check_smog_readiness;
  const titleIssues = sections.interior_questions?.subsections?.title_issues;
  const vinPlate = sections.exterior?.subsections?.tires?.subsections?.vin_plate;
  const licensePlate = sections.exterior?.subsections?.tires?.subsections?.license_plate_registration;

  const licensePlateExpiration = sections.exterior?.subsections?.tires?.images?.find(
    (img: any) => img.type === 'tires.license_plate_registration'
  )?.expiration_date;

  const hasObd = !!obd?.grade;
  const titleSelectedCheckboxes = (titleIssues?.checkboxes || []).filter((c: { selected: boolean }) => c.selected);
  const hasTitleSection = !!titleIssues?.grade || titleSelectedCheckboxes.length > 0;
  const hasVin = !!vinPlate?.grade;
  const hasLicense = !!licensePlate?.grade;

  if (!hasObd && !hasTitleSection && !hasVin && !hasLicense) return null;

  return (
    <ColumnCard>
      <ColumnHeader>OBD Check/Smog Readiness</ColumnHeader>
      {hasObd &&
        (() => {
          const colors = obd.grade ? gradeToColor[obd.grade] : null;
          return (
            <ItemCard>
              <ItemRow>
                <ItemIconWrapper>
                  <NotesIcon />
                </ItemIconWrapper>
                <ItemLabel>{obd.label}</ItemLabel>
                {colors && (
                  <GradeBadge bg={colors.bg} fg={colors.fg}>
                    {obd.grade}
                  </GradeBadge>
                )}
              </ItemRow>
              {obd.notes?.message && <SectionNoteText>{obd.notes.message}</SectionNoteText>}
            </ItemCard>
          );
        })()}

      {(hasTitleSection || hasVin || hasLicense) && (
        <>
          <ColumnHeader style={{ marginTop: 8, fontSize: 12 }}>Title &amp; Registration</ColumnHeader>
          {hasTitleSection &&
            (() => {
              const colors = titleIssues.grade ? gradeToColor[titleIssues.grade] : null;
              return (
                <ItemCard>
                  <ItemRow>
                    <ItemIconWrapper>
                      <NotesIcon />
                    </ItemIconWrapper>
                    <ItemLabel>{titleIssues.label}</ItemLabel>
                    {colors && (
                      <GradeBadge bg={colors.bg} fg={colors.fg}>
                        {titleIssues.grade}
                      </GradeBadge>
                    )}
                  </ItemRow>
                  {titleIssues.notes?.message && <SectionNoteText>{titleIssues.notes.message}</SectionNoteText>}
                  {titleSelectedCheckboxes.map((cb: { id: number; label: string }) => (
                    <CheckboxItem key={cb.id}>{cb.label}</CheckboxItem>
                  ))}
                </ItemCard>
              );
            })()}
          {hasVin &&
            (() => {
              const colors = vinPlate.grade ? gradeToColor[vinPlate.grade] : null;
              return (
                <ItemCard>
                  <ItemRow>
                    <ItemIconWrapper>
                      <NotesIcon />
                    </ItemIconWrapper>
                    <ItemLabel>{vinPlate.label}</ItemLabel>
                    {colors && (
                      <GradeBadge bg={colors.bg} fg={colors.fg}>
                        {vinPlate.grade}
                      </GradeBadge>
                    )}
                  </ItemRow>
                  {vinPlate.notes?.message && <SectionNoteText>{vinPlate.notes.message}</SectionNoteText>}
                </ItemCard>
              );
            })()}
          {hasLicense &&
            (() => {
              const colors = licensePlate.grade ? gradeToColor[licensePlate.grade] : null;
              return (
                <ItemCard>
                  <ItemRow>
                    <ItemIconWrapper>
                      <NotesIcon />
                    </ItemIconWrapper>
                    <ItemLabel>{licensePlate.label}</ItemLabel>
                    {licensePlateExpiration && (
                      <ExpirationText>{formatYearAndMonth(licensePlateExpiration)}</ExpirationText>
                    )}
                    {colors && (
                      <GradeBadge bg={colors.bg} fg={colors.fg}>
                        {licensePlate.grade}
                      </GradeBadge>
                    )}
                  </ItemRow>
                  {licensePlate.notes?.message && <SectionNoteText>{licensePlate.notes.message}</SectionNoteText>}
                </ItemCard>
              );
            })()}
        </>
      )}
    </ColumnCard>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

export const PrintView = () => {
  const vehicle = useViewedVehicle();
  const images = useImages();
  const vehicleId = useGetVehicleId();
  const { data: sections } = useGetSectionsQuery(vehicleId || skipToken);
  const currentUser = useCurrentUser();

  // ── section data (shared hooks — same logic as the web tab components) ───────
  // Must be called unconditionally before any early returns (Rules of Hooks)
  const { section: extGeneralSection, images: extGeneralImages } = useExteriorGeneralSection();
  const { section: tiresSection, orderedImages: tiresOrderedImages } = useTiresSection();
  const extDamageImages = useExteriorDamageImages();

  const { section: intGeneralSection, images: intGeneralImages } = useInteriorGeneralSection();
  const powerEquipmentSection = usePowerEquipmentSection();
  const intDamageImages = useInteriorDamageImages();

  const { section: underHoodSection, images: underHoodImages } = useUnderHoodSection();
  const { section: dashSectionData, images: dashImages } = useDashSection();
  const driveCyclesSection = useDriveCyclesSection();

  const { smogImages, vinImage, plateImage } = useOBDImages();

  const origin = formatAddress(currentUser);
  const destination = vehicle?.user ? formatAddress(vehicle.user) : '';

  const { data: route } = useGetListerRouteDistancesQuery(
    { origin, destinations: [destination], poolId: -1 },
    { skip: !currentUser?.role || !vehicle || !vehicle.user }
  );

  if (!vehicle) return null;

  const heroImage = images.find(i => i.type !== 'exterior.video' && i.url);
  const photoImages = images.filter(i => i.type !== 'exterior.video' && i.url);

  const obdImages = [...smogImages, ...(vinImage ? [vinImage] : []), ...(plateImage ? [plateImage] : [])];

  // ── column arrays ─────────────────────────────────────────────────────────────
  const exteriorColumns = [extGeneralSection, tiresSection].filter(Boolean) as Section[];
  const interiorColumns = [intGeneralSection, powerEquipmentSection].filter(Boolean) as Section[];
  const mechanicalColumns = [underHoodSection, dashSectionData, driveCyclesSection].filter(Boolean) as Section[];

  const hasExterior = exteriorColumns.some(s => Object.values(s.subsections || {}).some(i => !!i.grade));
  const hasInterior = interiorColumns.some(s => Object.values(s.subsections || {}).some(i => !!i.grade));
  const hasMechanical = mechanicalColumns.some(s => Object.values(s.subsections || {}).some(i => !!i.grade));
  const hasOBD = !!(
    sections?.interior_questions?.subsections?.odb_check_smog_readiness?.grade ||
    sections?.interior_questions?.subsections?.title_issues?.grade ||
    (sections?.interior_questions?.subsections?.title_issues?.checkboxes || []).some((c: any) => c.selected) ||
    sections?.exterior?.subsections?.tires?.subsections?.vin_plate?.grade ||
    sections?.exterior?.subsections?.tires?.subsections?.license_plate_registration?.grade
  );

  // ── sectioned image groups (last page) ────────────────────────────────────────
  const tiresDisplayImages = tiresOrderedImages.filter(
    (img): img is NonNullable<typeof img> => !!img?.url
  );

  type ImageGroup = { label: string; images: Section['images'] };

  const exteriorImageGroups: ImageGroup[] = [
    { label: 'Exterior Pictures', images: extGeneralImages },
    { label: 'Damage Pictures', images: extDamageImages },
    { label: 'Tires Pictures', images: tiresDisplayImages },
  ].filter(g => g.images.length > 0);

  const interiorImageGroups: ImageGroup[] = [
    { label: 'Interior Pictures', images: intGeneralImages },
    { label: 'Interior Damage Pictures', images: intDamageImages },
  ].filter(g => g.images.length > 0);

  const mechanicalImageGroups: ImageGroup[] = [
    { label: 'Under Hood Pictures', images: underHoodImages },
    { label: 'OBD Check/Smog Readiness Pictures', images: obdImages },
    { label: 'Dash Pictures', images: dashImages },
  ].filter(g => g.images.length > 0);

  const sectionedImageBlocks = [
    { title: 'Exterior Pictures', groups: exteriorImageGroups },
    { title: 'Interior Pictures', groups: interiorImageGroups },
    { title: 'Mechanical Pictures', groups: mechanicalImageGroups },
  ].filter(b => b.groups.length > 0);

  const autocheck = vehicle.autocheck_info;

  const { mileageValue, mileageIconType } = (() => {
    if (autocheck?.has_odometer_issue) return { mileageValue: 'Has Issue', mileageIconType: 'fail' as const };
    if (autocheck?.has_mileage_inconsistency)
      return { mileageValue: 'Mileage Inconsistency', mileageIconType: 'info' as const };
    return { mileageValue: 'No Issue', mileageIconType: 'success' as const };
  })();

  const autochecRows = autocheck
    ? [
        {
          Icon: OwnerIcon,
          label: 'Owner(s)',
          value: String(autocheck.owner_count ?? '—'),
          iconType: 'success' as const
        },
        {
          Icon: AccidentIcon,
          label: 'Accident / Damage',
          value: autocheck.has_accident ? 'Has damage/accident' : 'No damage/accident',
          iconType: (autocheck.has_accident ? 'info' : 'success') as 'info' | 'success' | 'fail'
        },
        {
          Icon: StateTitleBrandIcon,
          label: 'State Title Brand',
          value: autocheck.has_state_title_brand ? autocheck.state_title_brand || 'Yes' : 'Clean',
          iconType: (autocheck.has_state_title_brand ? 'fail' : 'success') as 'info' | 'success' | 'fail'
        },
        {
          Icon: UsageTypeIcon,
          label: 'Usage Type',
          value: autocheck.usage_type || '—',
          iconType: 'success' as const
        },
        {
          Icon: OdometerCheckIcon,
          label: 'Odometer Check',
          value: mileageValue,
          iconType: mileageIconType
        }
      ]
    : [];

  const subIconMap = {
    success: CheckConfirmedIcon,
    fail: FailIcon,
    info: AutoCheckInfoIcon
  };

  const characteristics = [
    { Icon: TrimIcon, label: 'Trim', value: vehicle.trim || '—' },
    { Icon: EngineIcon, label: 'Engine', value: vehicle.engine || '—' },
    { Icon: ExteriorIcon, label: 'Exterior', value: vehicle.ext_color || '—' },
    { Icon: TransmissionIcon, label: 'Transmission', value: vehicle.transmission || '—' },
    { Icon: DriveTrainIcon, label: 'Drive Train', value: vehicle.drive_train || '—' }
  ];

  const equipment = (vehicle.options || []).filter((o: any) => o.is_typical).map((o: any) => o.option_name);

  const seller = vehicle.user?.seller;
  const lister = vehicle.lister?.options;

  return createPortal(
    <>
      <PrintGlobalStyle />
      <Wrapper className="print-view">
        {/* Hero image */}
        {heroImage && (
          <HeroImageWrapper>
            <HeroImage src={heroImage.url} alt="Vehicle" />
          </HeroImageWrapper>
        )}

        {/* Title + VIN */}
        <VehicleTitle>{`${vehicle.year} ${vehicle.make} ${vehicle.model}`}</VehicleTitle>
        <VinText>VIN: {vehicle.vin}</VinText>

        {/* KBB stat cards */}
        <StatCardsRow>
          <StatCard bg="rgba(21, 182, 132, 0.15)">
            <StatIconWrapper>
              <DollarIcon />
            </StatIconWrapper>
            <StatValue>{vehicle.bluebook_value ? formatCurrency(vehicle.bluebook_value) : 'N/A'}</StatValue>
            <StatLabel>Value Without Miles Adj</StatLabel>
          </StatCard>
          <StatCard bg="rgba(253, 183, 20, 0.15)">
            <StatIconWrapper>
              <MoneyIcon />
            </StatIconWrapper>
            <StatValue>{vehicle.bluebook_mileage ? formatCurrency(vehicle.bluebook_mileage) : 'N/A'}</StatValue>
            <StatLabel>Blue Book@ Lending Value</StatLabel>
          </StatCard>
          <StatCard bg="rgba(57, 148, 222, 0.15)">
            <StatIconWrapper>
              <GasStationIcon />
            </StatIconWrapper>
            <StatValue>{vehicle.mileage ? vehicle.mileage.toLocaleString() : 'N/A'}</StatValue>
            <StatLabel>Mileage</StatLabel>
          </StatCard>
        </StatCardsRow>

        {/* AutoCheck */}
        {autocheck && (
          <>
            <SectionGap />
            <AutoCheckBlock>
              <AutoCheckLogoRow>
                <AutoCheckReportIcon />
              </AutoCheckLogoRow>
              {autochecRows.map(row => {
                const SubIcon = subIconMap[row.iconType];
                return (
                  <AutoCheckRow key={row.label}>
                    <AutoCheckIconBox>
                      <row.Icon />
                    </AutoCheckIconBox>
                    <AutoCheckLabel>{row.label}</AutoCheckLabel>
                    <AutoCheckValue>{row.value}</AutoCheckValue>
                    <SubIcon />
                  </AutoCheckRow>
                );
              })}
            </AutoCheckBlock>
          </>
        )}

        {vehicle.banners.filter(b => b.is_visible).length > 0 && (
          <div>
            {vehicle.banners
              .filter(b => b.is_visible)
              .map(banner => {
                const fg = ['#FFB469', '#FFC709'].includes(banner.color) ? '#000' : '#fff';
                return (
                  <PrintBanner key={banner.id} bg={banner.color} fg={fg}>
                    {banner.label}
                  </PrintBanner>
                );
              })}
          </div>
        )}

        {/* Vehicle Information */}
        <div style={{ breakInside: 'avoid' }}>
          <SectionGap />
          <SectionTitle>Vehicle Information</SectionTitle>
          <CharGrid>
            {characteristics.map(c => (
              <CharCard key={c.label}>
                <CharIconWrapper>
                  <c.Icon />
                </CharIconWrapper>
                <CharLabel>{c.label}</CharLabel>
                <CharValue>{c.value}</CharValue>
              </CharCard>
            ))}
          </CharGrid>
        </div>

        {/* Equipment — flows naturally after Vehicle Information */}
        {equipment.length > 0 && (
          <div style={{ breakInside: 'avoid' }}>
            <SectionGap />
            <EquipmentBlock>
              <SectionTitle>Equipment</SectionTitle>
              <EquipmentGrid>
                {equipment.map((e: string, i: number) => (
                  <EquipmentItem key={i}>{e}</EquipmentItem>
                ))}
              </EquipmentGrid>
            </EquipmentBlock>
          </div>
        )}

        {/* ── Page 3: Exterior + Interior (Interior flows after Exterior) ── */}
        {hasExterior && (
          <VehicleSectionBlock style={{ breakBefore: 'page' }}>
            <SectionTitle>Exterior</SectionTitle>
            <ColumnsRow>{exteriorColumns.map(subsection => renderColumn(subsection))}</ColumnsRow>
          </VehicleSectionBlock>
        )}

        {hasInterior && (
          <VehicleSectionBlock>
            <SectionTitle>Interior</SectionTitle>
            <ColumnsRow>{interiorColumns.map(s => renderTopLevelAsColumn(s))}</ColumnsRow>
          </VehicleSectionBlock>
        )}

        {/* ── Page 4: Mechanical + Seller Info ────────────────────────────
            Force a new page before Mechanical only when Interior exists.
            When Interior is absent, Mechanical flows with Exterior so they
            can share a page if there is room.                              */}
        {(hasMechanical || hasOBD) && (
          <VehicleSectionBlock style={{ breakBefore: hasInterior ? 'page' : 'auto' }}>
            <SectionTitle>Mechanical</SectionTitle>
            <ColumnsRow>
              {mechanicalColumns.map(s => renderTopLevelAsColumn(s))}
              {hasOBD && renderOBDColumn(sections)}
            </ColumnsRow>
          </VehicleSectionBlock>
        )}

        {/* Seller + Transport flow on the same page as Mechanical */}
        {vehicle.user && (
          <div style={{ breakInside: 'avoid' }}>
            <SectionGap />
            <TwoColBlocks>
              <InfoBlock>
                <BlockTitle>Seller Information</BlockTitle>
                <InfoRow>
                  <InfoRowLabel>
                    <InfoRowIconWrapper>
                      <ListerIcon />
                    </InfoRowIconWrapper>
                    Lister Name:
                  </InfoRowLabel>
                  <InfoRowValue>{vehicle.user.dealership_name || '—'}</InfoRowValue>
                </InfoRow>
                <InfoRow>
                  <InfoRowLabel>
                    <InfoRowIconWrapper>
                      <AddressIcon />
                    </InfoRowIconWrapper>
                    Address:
                  </InfoRowLabel>
                  <InfoRowValue>{formatAddress(vehicle.user)}</InfoRowValue>
                </InfoRow>
                <InfoRow>
                  <InfoRowLabel>
                    <InfoRowIconWrapper>
                      <PhoneIcon />
                    </InfoRowIconWrapper>
                    Phone Number:
                  </InfoRowLabel>
                  <InfoRowValue>
                    {vehicle.user.seller?.phone_number ? (
                      <PhoneNumberText text={vehicle.user.seller.phone_number} />
                    ) : (
                      'N/A'
                    )}
                  </InfoRowValue>
                </InfoRow>
                {route && (
                  <DistanceRow>
                    <InfoRowIconWrapper>
                      <HomeIcon />
                    </InfoRowIconWrapper>
                    <div>
                      <DistanceText>{`${route.distanceInMiles} miles away > ${vehicle.user.city}, ${vehicle.user.state}`}</DistanceText>
                      <HelperText>{`Approximate shipping quote from ${currentUser.city}, ${currentUser.state} to ${vehicle.user.city}, ${vehicle.user.state}`}</HelperText>
                    </div>
                  </DistanceRow>
                )}
              </InfoBlock>

              <InfoBlock>
                <BlockTitle>Transportation Cost</BlockTitle>
                <InfoRow>
                  <InfoRowLabel>
                    <InfoRowIconWrapper>
                      <AddressIcon />
                    </InfoRowIconWrapper>
                    Email:
                  </InfoRowLabel>
                  <InfoRowValue>{vehicle.user.email || '—'}</InfoRowValue>
                </InfoRow>
                <InfoRow>
                  <InfoRowLabel>
                    <InfoRowIconWrapper>
                      <PhoneIcon />
                    </InfoRowIconWrapper>
                    Phone Number:
                  </InfoRowLabel>
                  <InfoRowValue>
                    {vehicle.user.seller?.phone_number ? (
                      <PhoneNumberText text={vehicle.user.seller.phone_number} />
                    ) : (
                      'N/A'
                    )}
                  </InfoRowValue>
                </InfoRow>
                {route && (
                  <DistanceRow>
                    <InfoRowIconWrapper>
                      <HomeIcon />
                    </InfoRowIconWrapper>
                    <DistanceText>{`${route.distanceInMiles} miles away > ${vehicle.user.city}, ${vehicle.user.state}`}</DistanceText>
                  </DistanceRow>
                )}
                <InfoRow style={{ marginTop: 8 }}>
                  <InfoRowLabel style={{ flex: 1, alignItems: 'flex-start' }}>
                    CarsArrive is North America's largest hauler of used cars.
                  </InfoRowLabel>
                  <CarsArriveIcon />
                </InfoRow>
              </InfoBlock>
            </TwoColBlocks>
          </div>
        )}

        {/* ── Remaining sections: flow naturally, never split mid-block ─── */}
        {vehicle.inspector_notes?.message && (
          <SectionGap>
            <InfoBlock style={{ breakInside: 'avoid' }}>
              <SectionTitle>Inspector Comments</SectionTitle>
              <CommentText>{vehicle.inspector_notes.message}</CommentText>
            </InfoBlock>
          </SectionGap>
        )}

        {seller && (
          <SectionGap>
            <InfoBlock style={{ breakInside: 'avoid' }}>
              <SectionTitle>Payment / Pick Up Instructions</SectionTitle>
              <PaymentRow>
                <PaymentLabel>Payment</PaymentLabel>
                <PaymentValue>
                  {seller.purchase_options?.length > 0
                    ? seller.purchase_options.map((o: any) => o.displayName).join(', ')
                    : 'N/A'}
                </PaymentValue>
              </PaymentRow>
              {seller.pickup_instruction && (
                <PaymentRow>
                  <PaymentLabel>Pick Up</PaymentLabel>
                  <PaymentValue>{seller.pickup_instruction}</PaymentValue>
                </PaymentRow>
              )}
            </InfoBlock>
          </SectionGap>
        )}

        <SaleTermsWrapper style={{ breakInside: 'avoid' }}>
          <SaleTermsTitle>Vehicle Sale Terms</SaleTermsTitle>
          {vehicle.is_guaranteed ? (
            <SaleTermsText>
              Vehicle is <HighlightedText isGuaranteed>Green Light</HighlightedText>. Engine, transmission and rear end
              guaranteed for 24hrs (Vehicles being transported over 300 miles are guaranteed until 5pm on the Wednesday
              following the auction).
            </SaleTermsText>
          ) : (
            <SaleTermsText>
              Vehicle is being sold <HighlightedText>AS-IS:</HighlightedText> Buyer is responsible for viewing vehicle
              prior to bidding
            </SaleTermsText>
          )}
        </SaleTermsWrapper>

        {lister && (
          <SectionGap>
            <InfoBlock style={{ breakInside: 'avoid' }}>
              <SectionTitle>Lister Information</SectionTitle>
              <ListerRow>
                <ListerAvatar>
                  {lister.avatar ? <img src={lister.avatar} alt="avatar" /> : <EmptyAvatarPlaceholder />}
                </ListerAvatar>
                <div>
                  <ListerName>{lister.name_for_bio ?? 'N/A'}</ListerName>
                  <ListerBio>{lister.bio ?? ''}</ListerBio>
                </div>
              </ListerRow>
            </InfoBlock>
          </SectionGap>
        )}

        {/* All car photos */}
        {photoImages.length > 0 && (
          <PhotosSection>
            <SectionTitle>All car photos</SectionTitle>
            <PhotosGrid>
              {photoImages.map((img, i) => (
                <PhotoItem key={img.id ?? i} src={img.url} alt={`Vehicle photo ${i + 1}`} />
              ))}
            </PhotosGrid>
          </PhotosSection>
        )}

        {/* Sectioned images */}
        {sectionedImageBlocks.length > 0 && (
          <SectionedPhotosSection>
            {sectionedImageBlocks.map((block, bi) => (
              <ImageGroupBlock key={block.title}>
                <SectionTitle style={bi > 0 ? { breakBefore: 'auto' } : undefined}>{block.title}</SectionTitle>
                {block.groups.map(group => (
                  <ImageSubGroupBlock key={group.label}>
                    <ImageSubGroupLabel>{group.label}</ImageSubGroupLabel>
                    <ImageStrip>
                      {group.images.map((img, i) => (
                        <SectionedPhoto key={img.id ?? i} src={img.url} alt={group.label} />
                      ))}
                    </ImageStrip>
                  </ImageSubGroupBlock>
                ))}
              </ImageGroupBlock>
            ))}
          </SectionedPhotosSection>
        )}
      </Wrapper>
    </>,
    document.body
  );
};
